// Print success page — hit after Stripe redirects from a successful one-time
// poster checkout. Calls /api/prints/confirm/{session_id} which is idempotent
// and triggers the Gelato order creation if not yet done.
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight, Printer } from "lucide-react";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { useT } from "@/lib/i18n";
import { apiFetch } from "@/lib/api";

const PrintSuccessPage = () => {
  const t = useT();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("loading"); // loading | paid | unpaid | error
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    (async () => {
      const { ok, data: d } = await apiFetch(`/api/prints/confirm/${sessionId}`);
      if (!ok) {
        setStatus("error");
        return;
      }
      setData(d);
      setStatus(d.payment_status === "paid" ? "paid" : "unpaid");
    })();
  }, [sessionId]);

  return (
    <Layout testId="page-print-success">
      <SEO title={`${t("print.success_title")} — geoVINUM`} description={t("print.success_subtitle")} />
      <section className="min-h-[70vh] flex items-center justify-center bg-[color:var(--gv-mist)] py-20 px-6">
        <div className="max-w-xl w-full bg-white border border-[color:var(--gv-line)] rounded-[10px] p-8 md:p-12 text-center shadow-sm">
          {status === "loading" && (
            <>
              <Loader2 size={32} className="animate-spin mx-auto text-[color:var(--gv-pink)] mb-5" />
              <h1 className="font-display text-2xl text-[color:var(--gv-ink)]">{t("print.success_pending")}</h1>
            </>
          )}

          {status === "paid" && (
            <>
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[color:var(--gv-pink)]/10 mb-5">
                <CheckCircle2 size={28} className="text-[color:var(--gv-pink)]" />
              </div>
              <h1 className="font-display text-3xl md:text-4xl text-[color:var(--gv-ink)] mb-3" data-testid="print-success-title">
                {t("print.success_title")}
              </h1>
              <p className="text-[color:var(--gv-ink)]/70 leading-relaxed mb-6">
                {t("print.success_subtitle")}
              </p>
              {data?.gelato_order_id && (
                <div className="inline-flex items-center gap-2 bg-[color:var(--gv-mist)] px-3 py-1.5 rounded-full text-[12px] text-[color:var(--gv-ink)]/70 mb-6">
                  <Printer size={12} /> Gelato ID&nbsp;: <span className="font-mono text-[color:var(--gv-ink)]">{data.gelato_order_id}</span>
                </div>
              )}
              {data?.fulfillment_error && !data?.gelato_order_id && (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 text-[12.5px] px-3 py-2 rounded-[5px] mb-6 text-left" data-testid="print-fulfillment-warning">
                  Votre paiement est confirmé, mais nous finalisons encore la commande d'impression. Notre équipe vous contactera sous 24&nbsp;h.
                </div>
              )}
              <div>
                <Link to={`/products/${data?.slug || "atlas-magnum"}`} className="btn-ghost text-[13px]" data-testid="print-success-back">
                  {t("print.success_back")} <ArrowRight size={14} />
                </Link>
              </div>
            </>
          )}

          {(status === "unpaid" || status === "error") && (
            <>
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-50 mb-5">
                <XCircle size={28} className="text-red-500" />
              </div>
              <h1 className="font-display text-2xl text-[color:var(--gv-ink)] mb-3">{t("print.success_failed")}</h1>
              <Link to="/explore" className="btn-primary">{t("print.success_back")} <ArrowRight size={14} /></Link>
            </>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default PrintSuccessPage;
